/**
 * Format: Price
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API money and price responses.
 *
 */

/**
 * Format money v2.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/MoneyV2
 * @param {Object} price - MoneyV2 response.
 * @returns {Object}
 */
export function formatMoneyV2(price) {
  if (!price) {
    return {
      amount: false,
      currencyCode: false,
    }
  }

  return {
    amount: formatPriceAmount(price.amount),
    currencyCode: price.currencyCode,
  }
}

/**
 * Format a price amount.
 * - If the amount can be converted to a number, it is converted and multiplied
 *   by 100 to get the cents amount.
 * - Otherwise returns `false`.
 * @param {*} amount - Amount value to process.
 * @returns {Number|Boolean}
 */
export function formatPriceAmount(amount) {
  // eslint-disable-next-line no-undefined
  if (amount === null || amount === undefined) {
    return false
  }

  const numericAmount = Number(amount)

  if (isNaN(numericAmount)) {
    return false
  }

  return numericAmount * 100
}

/**
 * Format product price range.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/ProductPriceRange
 * @param {Object} price - ProductPriceRange response.
 * @returns {Object}
 */
export function formatProductPriceRange(price) {
  return {
    amount: formatPriceAmount(price?.minVariantPrice?.amount),
    currencyCode: price?.minVariantPrice?.currencyCode,
    max: formatPriceAmount(price?.maxVariantPrice?.amount),
    min: formatPriceAmount(price?.minVariantPrice?.amount),
    varies: price?.maxVariantPrice?.amount !== price?.minVariantPrice?.amount,
  }
}

/**
 * Resolve a market-aware numeric price from an Algolia Shopify record.
 * - Prefers `market_pricing_<currency>` fields when Shopify Markets indexing
 *   is enabled, then falls back to legacy flat fields.
 * @param {Object} record - Algolia hit record.
 * @param {Object} options - Resolver options.
 * @param {String} options.currencyCode - Active storefront currency code.
 * @param {Array} options.marketFieldCandidates
 *   - Field names to try in market pricing object.
 * @param {Array} options.fallbackFieldCandidates
 *   - Flat field names to try on root record.
 * @returns {Number|null}
 */
export function resolveAlgoliaMarketPrice(record, options = {}) {
  const {
    currencyCode,
    marketFieldCandidates = [],
    fallbackFieldCandidates = [],
  } = options

  const currency = String(currencyCode || '').trim().toLowerCase()

  if (record && currency) {
    const marketContainers = [
      record[`market_pricing_${currency}`],
      record.market_pricing?.[currency],
      record.market_pricing?.[currency.toUpperCase()],
    ]

    for (const container of marketContainers) {
      if (!container || typeof container !== 'object') {
        continue
      }

      const marketValue = getFirstNumericValue(container, marketFieldCandidates)
      if (marketValue !== null) {
        return marketValue
      }
    }
  }

  return getFirstNumericValue(record, fallbackFieldCandidates)
}

/**
 * Return the first valid numeric value for the provided keys.
 * @param {Object} source - Source object.
 * @param {Array} keys - Candidate keys in priority order.
 * @returns {Number|null}
 */
function getFirstNumericValue(source, keys = []) {
  if (!source || typeof source !== 'object') {
    return null
  }

  for (const key of keys) {
    const value = toNumericValue(source[key])
    if (value !== null) {
      return value
    }
  }

  return null
}

/**
 * Convert a value to a finite number.
 * @param {*} value - Raw value.
 * @returns {Number|null}
 */
function toNumericValue(value) {
  // eslint-disable-next-line no-undefined
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return null
  }

  return numericValue
}
